import React from 'react';

import { InputAdornment, Popover } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import { StyledTextField } from 'components/Input/styles';

import { OrgUnit, unitPath } from '../types';

import UnitTreePicker from './UnitTreePicker';
import { collectExpandable } from '../types';
import { PickerPopoverBody } from './styles';

export interface UnitPickerFieldProps {
  id: string;
  units: OrgUnit[];
  value: string | null;
  onChange: (unitId: string) => void;
  placeholder: string;
  /** Names the field for assistive technology when its visible label sits elsewhere. */
  label: string;
  disabled?: boolean;
  /** Units that cannot be chosen, with the reason shown on hover. */
  disabledReasons?: Map<string, string>;
  size?: 'small' | 'medium';
}

/** A closed field showing the chosen unit's path, which opens the unit search and tree. */
const UnitPickerField: React.FunctionComponent<UnitPickerFieldProps> = ({
  id,
  units,
  value,
  onChange,
  placeholder,
  label,
  disabled,
  disabledReasons,
  size,
}) => {
  const fieldRef = React.useRef<HTMLDivElement>(null);
  const [open, setOpen] = React.useState(false);
  // Everything expanded: the picker is for choosing a parent, and a collapsed tree hides the choices.
  const [expandedIds, setExpandedIds] = React.useState<Set<string>>(() => collectExpandable(units));

  const openPicker = (): void => {
    if (!disabled) setOpen(true);
  };

  return (
    <>
      <StyledTextField
        ref={fieldRef}
        id={id}
        fullWidth
        size={size}
        disabled={disabled}
        value={value ? unitPath(units, value) : ''}
        placeholder={placeholder}
        onClick={openPicker}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
            event.preventDefault();
            openPicker();
          }
        }}
        inputProps={{ readOnly: true, 'aria-label': label, 'aria-haspopup': 'dialog', 'aria-expanded': open }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <ArrowDropDownIcon color="action" />
            </InputAdornment>
          ),
        }}
      />
      <Popover
        open={open}
        anchorEl={fieldRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <PickerPopoverBody role="dialog" aria-label={label} $width={fieldRef.current?.offsetWidth}>
          <UnitTreePicker
            units={units}
            selectedId={value}
            disabledReasons={disabledReasons}
            expandedIds={expandedIds}
            onToggleExpand={(unitId) =>
              setExpandedIds((previous) => {
                const next = new Set(previous);
                if (!next.delete(unitId)) next.add(unitId);
                return next;
              })
            }
            onSelect={(unitId) => {
              onChange(unitId);
              setOpen(false);
            }}
          />
        </PickerPopoverBody>
      </Popover>
    </>
  );
};

export default UnitPickerField;
